import React from 'react';

/**
 * AssessmentStep4Summary
 *
 * Props:
 * - facilityInfo: object with facility fields
 * - energyBaseline: object with energy and production baseline fields
 * - answers: object mapping questionId -> answer
 * - questions: array of question objects
 */
const AssessmentStep4Summary = ({
  facilityInfo,
  energyBaseline,
  answers,
  questions
}) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-8">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
        Review Your Assessment
      </h2>

      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Facility Information
          </h3>
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <p className="text-gray-900 dark:text-gray-100">
              <strong>Company:</strong> {facilityInfo.companyName}
            </p>
            <p className="text-gray-900 dark:text-gray-100">
              <strong>Contact:</strong> {facilityInfo.contactName}
            </p>
            <p className="text-gray-900 dark:text-gray-100">
              <strong>Email:</strong> {facilityInfo.email}
            </p>
            <p className="text-gray-900 dark:text-gray-100">
              <strong>Major Industry:</strong> {facilityInfo.majorIndustry}
            </p>
            <p className="text-gray-900 dark:text-gray-100">
              <strong>Sub-Industry:</strong> {facilityInfo.subIndustry}
            </p>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Energy &amp; Production Baseline
          </h3>
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
              <div>
                <p className="text-gray-900 dark:text-gray-100">
                  <strong>Annual kWh:</strong>
                </p>
                <p className="text-gray-900 dark:text-gray-100">
                  {energyBaseline.annualKwh
                    ? Number(energyBaseline.annualKwh).toLocaleString()
                    : 'Not provided'}
                </p>
              </div>
              <div>
                <p className="text-gray-900 dark:text-gray-100">
                  <strong>Annual Cost:</strong>
                </p>
                <p className="text-gray-900 dark:text-gray-100">
                  {energyBaseline.annualElectricityCost
                    ? `$${Number(energyBaseline.annualElectricityCost).toLocaleString()}`
                    : 'Not provided'}
                </p>
              </div>
              <div>
                <p className="text-gray-900 dark:text-gray-100">
                  <strong>Monthly Bill:</strong>
                </p>
                <p className="text-gray-900 dark:text-gray-100">
                  {energyBaseline.averageMonthlyBill
                    ? `$${Number(energyBaseline.averageMonthlyBill).toLocaleString()}`
                    : 'Not provided'}
                </p>
              </div>
              <div>
                <p className="text-gray-900 dark:text-gray-100">
                  <strong>Facility Size:</strong>
                </p>
                <p className="text-gray-900 dark:text-gray-100">
                  {energyBaseline.facilitySize
                    ? `${Number(energyBaseline.facilitySize).toLocaleString()} sq ft`
                    : 'Not provided'}
                </p>
              </div>
              <div>
                <p className="text-gray-900 dark:text-gray-100">
                  <strong>Operating Hours:</strong>
                </p>
                <p className="text-gray-900 dark:text-gray-100">
                  {energyBaseline.operatingHours
                    ? `${energyBaseline.operatingHours}/week`
                    : 'Not provided'}
                </p>
              </div>
              <div>
                <p className="text-gray-900 dark:text-gray-100">
                  <strong>Peak Demand:</strong>
                </p>
                <p className="text-gray-900 dark:text-gray-100">
                  {energyBaseline.peakDemandKw
                    ? `${energyBaseline.peakDemandKw} kW`
                    : 'Not provided'}
                </p>
              </div>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
              <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                Production Metrics
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-gray-900 dark:text-gray-100">
                    <strong>Annual Production:</strong>
                  </p>
                  <p className="text-gray-900 dark:text-gray-100">
                    {energyBaseline.annualProduction
                      ? `${Number(energyBaseline.annualProduction).toLocaleString()} ${
                          energyBaseline.productionUnit
                        }`
                      : 'Not provided'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-900 dark:text-gray-100">
                    <strong>Production Rate:</strong>
                  </p>
                  <p className="text-gray-900 dark:text-gray-100">
                    {energyBaseline.averageProductionRate
                      ? `${energyBaseline.averageProductionRate} ${energyBaseline.productionUnit}/hour`
                      : 'Not provided'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-900 dark:text-gray-100">
                    <strong>Capacity Utilization:</strong>
                  </p>
                  <p className="text-gray-900 dark:text-gray-100">
                    {energyBaseline.productionCapacity
                      ? `${energyBaseline.productionCapacity}%`
                      : 'Not provided'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-900 dark:text-gray-100">
                    <strong>Production Pattern:</strong>
                  </p>
                  <p className="text-gray-900 dark:text-gray-100">
                    {energyBaseline.productionSeasons || 'Not provided'}
                  </p>
                </div>
                {energyBaseline.annualRevenue && (
                  <div>
                    <p className="text-gray-900 dark:text-gray-100">
                      <strong>Annual Revenue:</strong>
                    </p>
                    <p className="text-gray-900 dark:text-gray-100">
                      ${Number(energyBaseline.annualRevenue).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>

              {energyBaseline.annualProduction && energyBaseline.annualKwh && (
                <div className="mt-3 p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                  <p className="text-sm text-emerald-800 dark:text-emerald-200">
                    <strong>Energy Productivity:</strong>{' '}
                    {(Number(energyBaseline.annualKwh) /
                      Number(energyBaseline.annualProduction)
                    ).toFixed(1)}{' '}
                    kWh per {energyBaseline.productionUnit}
                  </p>
                </div>
              )}
            </div>

            {energyBaseline.annualGasUsage && (
              <p className="mt-3 text-gray-900 dark:text-gray-100">
                <strong>Gas Usage:</strong>{' '}
                {Number(energyBaseline.annualGasUsage).toLocaleString()}{' '}
                {energyBaseline.gasUsageUnit}
                /year
              </p>
            )}
          </div>
        </div>

        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Assessment Responses
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            You have completed {Object.keys(answers).length} of {questions.length} questions.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AssessmentStep4Summary;


